import React, { useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { PapiUser } from '@wix/da-papi-types';
import { useMeasuredCookie } from '@wix/da-shared-react/pkg/Measured/useMeasuredCookie';
import { ZoneType } from '../../../../types/layout';

import Llama from './_partials/Llama';
import Background from './_partials/Background';
import GiveLlamaButton from './_partials/GiveLlamaButton';

import { LlamaPosition, DefaultLlama } from './types';
import {
  DEFAULT_BACKGROUND,
  BACKGROUND_RATIO,
  MAX_BACKGROUND_HEIGHT,
  MIN_BACKGROUND_HEIGHT,
} from './constants';
import { getMeasuredCookieType } from './helpers';

import s from './LlamaFarm.scss';

export interface Props {
  isOwner: boolean;
  className?: string;
  title?: string;
  showConfigMenu?: boolean;
  profileOwnerUser?: PapiUser;
  isCurrentUserCoreMember?: boolean;
  isNewlyAddedModule: boolean;
  backgroundIndex: number;
  llamas: LlamaPosition[];
  zone: ZoneType;
}

const LlamaFarm: React.FC<Props> = ({
  zone,
  llamas,
  className,
  backgroundIndex = DEFAULT_BACKGROUND,
}) => {
  const [giftedLlama, setGiftedLlama] = useState<LlamaPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { ref: inViewRef, inView } = useInView();
  const measuredContainer = useMeasuredCookie(
    getMeasuredCookieType(zone),
    containerRef,
    {
      cookieDim: 'width',
      listenOnWindow: true,
      sentinelRef,
    }
  );
  const giveLlama = useCallback(() => setGiftedLlama(DefaultLlama), []);
  // LlamaFarm has a fixed aspect ratio.
  // It is fluid for the heights between MIN_BACKGROUND_HEIGHT..MAX_BACKGROUND_HEIGHT.
  // The width is calculdated from the height to preserve the aspect ratio.
  const containerWidth = measuredContainer?.width ?? 300;
  const containerHeight = containerWidth / BACKGROUND_RATIO;
  const height = Math.min(
    MAX_BACKGROUND_HEIGHT,
    Math.max(containerHeight, MIN_BACKGROUND_HEIGHT)
  );
  const width = height * BACKGROUND_RATIO;
  const scale = height / MIN_BACKGROUND_HEIGHT;

  return (
    <>
      <div ref={sentinelRef} style={{ maxWidth: '100%' }} />
      <div ref={containerRef} className={classnames(s['root'], className)}>
        <div
          ref={inViewRef}
          className={s['wrapper']}
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <Background backgroundIndex={backgroundIndex}>
            {inView &&
              llamas.map((llama, index) => (
                <Llama
                  key={`${index}-${scale}`}
                  llama={llama}
                  scale={scale}
                  animation="hopping"
                  animationDelay={(index / llamas.length) * 6000}
                />
              ))}
            {inView && giftedLlama && (
              <Llama llama={giftedLlama} scale={scale} animation="gifted" />
            )}
          </Background>
          <GiveLlamaButton
            className={s['give-button']}
            onLlamaGiven={giveLlama}
          />
        </div>
      </div>
    </>
  );
};

LlamaFarm.displayName = 'LlamaFarm';

export default LlamaFarm;
